import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LayoutDashboard, Users, LogOut, Database, FolderOpen, UsersRound, Settings as SettingsIcon, ScrollText, BarChart3, MessageSquare, CalendarDays, ListTodo, ClipboardList, Contact, CalendarClock, Stethoscope, PhoneCall, FileText } from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/calls", label: "Calls", icon: PhoneCall },
  { to: "/intake", label: "Intake", icon: ClipboardList },
  { to: "/docket", label: "Docket", icon: CalendarClock },
  { to: "/medical", label: "Medical", icon: Stethoscope },
  { to: "/contacts", label: "Contacts", icon: Contact },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/tasks", label: "Tasks", icon: ListTodo },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/templates", label: "Templates", icon: FileText, adminOnly: true },
  { to: "/users", label: "Employees", icon: UsersRound, adminOnly: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText, adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const prevUnreadRef = useRef(null);

  const handleLogout = () => { logout(); navigate("/login"); };

  // Poll unread count; raise a lightweight toast whenever new messages/assignment
  // notifications arrive while the user is away from the Messages page.
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!active) return;
        const count = data.count;
        const prev = prevUnreadRef.current;
        if (prev !== null && count > prev && location.pathname !== "/messages") {
          try {
            const { data: convs } = await api.get("/conversations");
            const top = convs
              .filter((c) => c.unread > 0)
              .sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at))[0];
            if (top) {
              toast.message(top.name || "New message", {
                description: top.last_message_preview || "You have a new message",
                action: { label: "Open", onClick: () => navigate(`/messages?c=${top.id}`) },
              });
            }
          } catch (err) { console.debug("Message preview fetch failed (ignored):", err); }
        }
        prevUnreadRef.current = count;
        setUnread(count);
      } catch (err) { console.debug("Unread-count poll failed (ignored):", err); }
    };
    poll();
    const t = setInterval(poll, 6000);
    return () => { active = false; clearInterval(t); };
  }, [location.pathname, navigate]);

  const visibleNav = useMemo(
    () => nav.filter((item) => !item.adminOnly || user?.role === "admin"),
    [user?.role],
  );

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col fixed h-full" data-testid="sidebar">
        <div className="px-6 py-6 flex items-center gap-2.5 border-b border-gray-100">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center rounded-sm">
            <Database className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight text-gray-900">ClientCases</span>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {visibleNav.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold transition-colors ${
                  active ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                {item.label}
                {item.to === "/messages" && unread > 0 && (
                  <span data-testid="nav-messages-unread" className={`ml-auto text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${active ? "bg-white text-[#002FA7]" : "bg-[#002FA7] text-white"}`}>
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-gray-100 p-3">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">{children}</main>
    </div>
  );
}
